// src/components/PlayerRankingModal.jsx
// Historique des classements d'un joueur + mise à jour (ouverte à tous les utilisateurs)
import React, { useState, useEffect, useCallback } from 'react';
import { refs } from '../data/refs';
import { getPlayerRankingHistory, updatePlayerRanking } from '../services/playersService';

const label = (code) => {
  if (!code) return '';
  const f = refs.refClassement.find(c => c.code === String(code).trim());
  return f ? f.labelFr : code;
};
const fmtDate = (d) => {
  if (!d) return '';
  const [y, m, day] = String(d).slice(0, 10).split('-');
  return `${day}/${m}/${y}`;
};
const SOURCE = { manual: 'Saisie', match: 'Match', backfill: 'Match' };

export default function PlayerRankingModal({ player, onClose, onUpdated }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [classement, setClassement] = useState(player.dernierClassementCode || '');
  const [club, setClub] = useState(player.dernierClub || '');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const r = await getPlayerRankingHistory(player.id);
    if (r.success) setHistory(r.history);
    setLoading(false);
  }, [player.id]);
  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    setError(''); setInfo('');
    if (!classement) { setError('Choisissez un classement.'); return; }
    setSaving(true);
    const r = await updatePlayerRanking(player.id, { classementCode: classement, club: club || null, date, source: 'manual' });
    setSaving(false);
    if (!r.success) { setError(r.error); return; }
    setInfo(r.applied
      ? '✅ Classement mis à jour.'
      : 'ℹ️ Enregistré dans l\'historique (la fiche garde un classement plus récent).');
    await load();
    if (onUpdated) onUpdated();
  };

  const th = { textAlign: 'left', padding: '6px 8px', fontSize: '12px', color: '#666', borderBottom: '2px solid #ddd' };
  const td = { padding: '7px 8px', fontSize: '13px', borderBottom: '1px solid #eee' };
  const input = { width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #ccc', fontSize: '14px', boxSizing: 'border-box' };
  const name = [player.prenom, player.nom].filter(Boolean).join(' ');

  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '12px' }}>
      <div style={{ backgroundColor: '#fff', color: '#222', borderRadius: '12px', padding: '16px', width: '100%', maxWidth: '520px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 30px rgba(0,0,0,0.3)', fontFamily: 'Arial, sans-serif' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <h3 style={{ margin: 0, fontSize: '17px' }}>📈 Classement — {name}</h3>
          <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer' }}>✕</button>
        </div>

        <div style={{ backgroundColor: '#f5f9ff', border: '1px solid #cfe3fa', borderRadius: '8px', padding: '10px', marginBottom: '14px' }}>
          <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}>Mettre à jour le classement</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
            <div>
              <label style={{ fontSize: '12px', color: '#666' }}>Classement</label>
              <select style={input} value={classement} onChange={e => setClassement(e.target.value)}>
                <option value="">—</option>
                {refs.refClassement.map(c => <option key={c.code} value={c.code}>{c.labelFr}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: '12px', color: '#666' }}>Valable à partir du</label>
              <input type="date" style={input} value={date} max={new Date().toISOString().slice(0, 10)} onChange={e => setDate(e.target.value)} />
            </div>
          </div>
          <div style={{ marginTop: '8px' }}>
            <label style={{ fontSize: '12px', color: '#666' }}>Club (facultatif)</label>
            <input type="text" style={input} value={club} onChange={e => setClub(e.target.value)} />
          </div>
          {error && <div style={{ color: '#c62828', fontSize: '13px', marginTop: '8px' }}>{error}</div>}
          {info && <div style={{ color: '#2e7d32', fontSize: '13px', marginTop: '8px' }}>{info}</div>}
          <button type="button" onClick={handleSave} disabled={saving}
            style={{ width: '100%', marginTop: '10px', padding: '10px', borderRadius: '8px', border: 'none', backgroundColor: '#1976D2', color: '#fff', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}>
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>

        <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '6px' }}>Historique</div>
        {loading ? <p style={{ color: '#888', fontSize: '13px' }}>Chargement...</p>
          : history.length === 0 ? <p style={{ color: '#888', fontSize: '13px' }}>Aucun historique pour le moment.</p>
          : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr><th style={th}>Date</th><th style={th}>Classement</th><th style={th}>Club</th><th style={th}>Origine</th></tr></thead>
                <tbody>
                  {history.map(h => (
                    <tr key={h.id}>
                      <td style={td}>{fmtDate(h.effectiveDate)}</td>
                      <td style={td}><strong>{label(h.classementCode)}</strong></td>
                      <td style={td}>{h.club || ''}</td>
                      <td style={td}>{SOURCE[h.source] || h.source}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>
    </div>
  );
}